// src/pages/Leads/CreateLead.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLazySelectOptions } from "../../hooks/useLazySelectOptions";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  Grid,
  Autocomplete,
  Skeleton,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageLayout from "../../components/layout/PageLayout";
import { extractList, extractData } from "../../utils/response";
import { getValidationErrorDetails } from "../../utils/errorHandler";
import { useAuth } from "../../hooks/useAuth";
import {
  useLazyGetCountriesQuery,
  useLazyGetCompaniesQuery,
  useLazyGetLeadsStatusQuery,
  useLazyGetLeadSourcesQuery,
  useLazyGetLeadBrandsQuery,
  useLazyGetLeadByIdQuery,
  useLazyGetLeadAssignedUsersQuery,
  useLazyGetUsersByRoleQuery,
  useCreateLeadMutation,
  useUpdateLeadMutation,
} from "../../store/api";
// import LeadAdditionalDetailsSection from "../../components/Leads/LeadAdditionalDetailsSection";
import {
  resolveLeadStatusId,
  matchOptionId,
  findOptionById,
  idsEqual,
  enrichSelectOptionsFromLead,
} from "../../components/Leads/leadFormHelpers";

const mapIdNameList = (response) => {
  const rawList = extractList(response);
  return (Array.isArray(rawList) ? rawList : []).map((item) => ({
    id: item?.id,
    name: item?.name,
  }));
};

const mapCountriesOrCompanies = (data) => {
  const rawList = data?.data?.list ?? data?.list ?? extractList(data);
  return (Array.isArray(rawList) ? rawList : []).map((c) => ({
    id: c?.id,
    name: c?.name,
  }));
};

const mapSalesUsers = (payload) => {
  const users = Array.isArray(payload)
    ? payload
    : (payload?.data ?? payload?.users ?? payload?.results ?? []);
  if (!Array.isArray(users) || users.length === 0) return [];
  return users.map((user) => ({
    id: user.uuid || user.id || user._id,
    name: user.name || user.fullName || user.username || "—",
    role: user.roles?.[0]?.name || user.role || "Sales",
  }));
};

const findRevivePayBrand = (list) =>
  (list || []).find(
    (b) =>
      (b.name || "").toLowerCase().replace(/\s/g, "") === "revivepay" ||
      (b.name || "").toLowerCase().includes("revive pay"),
  );

const findPendingStatus = (list) =>
  (list || []).find((s) => (s.name || "").toLowerCase() === "pending");

function extractLeadAttachmentIds(leadData) {
  if (Array.isArray(leadData.attachments) && leadData.attachments.length > 0) {
    return leadData.attachments
      .map((a) => (a && typeof a === "object" ? a.id : a))
      .filter(Boolean);
  }
  if (Array.isArray(leadData.attachment_ids)) {
    return leadData.attachment_ids.filter(Boolean);
  }
  return [];
}

/** Maps API lead (nested or legacy flat) into CreateLead form state */
function mapLeadApiToForm(leadData) {
  const dba =
    leadData.dba && typeof leadData.dba === "object" ? leadData.dba : {};
  const legal =
    (leadData.legal_information &&
    typeof leadData.legal_information === "object"
      ? leadData.legal_information
      : null) ||
    (leadData.legalInformation && typeof leadData.legalInformation === "object"
      ? leadData.legalInformation
      : {}) ||
    {};
  const merchant =
    leadData.merchant && typeof leadData.merchant === "object"
      ? leadData.merchant
      : {};
  const bank =
    (leadData.bank_account && typeof leadData.bank_account === "object"
      ? leadData.bank_account
      : null) ||
    (leadData.bankAccount && typeof leadData.bankAccount === "object"
      ? leadData.bankAccount
      : {}) ||
    {};
  const equipmentList = leadData.equipment ?? leadData.equipments;
  const eq0 =
    Array.isArray(equipmentList) && equipmentList.length
      ? equipmentList[0]
      : {};

  const submission = leadData.submission_date ?? dba.submission_date;

  return {
    dba_name: leadData.dba_name || "",
    name: leadData.name || "",
    email: leadData.email || "",
    phone: leadData.phone || "",
    address: leadData.address || "",
    description: leadData.description || "",
    status_id: resolveLeadStatusId(leadData),
    source_id: leadData.source?.id ?? leadData.source_id ?? "",
    brand_id: leadData.brand?.id ?? leadData.brand_id ?? "",
    company_id: leadData.company_id ?? leadData.company?.id ?? "",
    country_id: leadData.country?.id ?? leadData.country_id ?? "",
    submission_date: submission ? String(submission).slice(0, 10) : "",
    legal_business_name: leadData.legal_business_name ?? legal.name ?? "",
    // dba_name: leadData.dba_name ?? dba.dba_name ?? "",
    federal_tax_id: leadData.federal_tax_id ?? merchant.federal_tax_id ?? "",
    business_years: leadData.business_years ?? merchant.business_years ?? "",
    business_months: leadData.business_months ?? merchant.business_months ?? "",
    business_category:
      leadData.business_category ?? merchant.business_category ?? "",
    monthly_volume: leadData.monthly_volume ?? dba.monthly_volume ?? "",
    equipment_type_id:
      leadData.equipment_type_id ?? eq0.equipment_type_id ?? "",
    manufacturer_id: leadData.manufacturer_id ?? eq0.manufacturer_id ?? "",
    equipment_model_id:
      leadData.equipment_model_id ?? eq0.equipment_model_id ?? "",
    personal_phone: leadData.personal_phone ?? dba.phone ?? dba.phone ?? "",
    legal_business_phone: leadData.legal_business_phone ?? legal.phone ?? "",
    legal_business_address:
      leadData.legal_business_address ?? legal.address ?? "",
    legal_business_email: leadData.legal_business_email ?? legal.email ?? "",
    monthly_lease: leadData.monthly_lease ?? dba.monthly_lease ?? "",
    is_bundled:
      leadData.is_bundled === true ||
      leadData.is_bundled === 1 ||
      dba.is_bundled === true ||
      dba.is_bundled === 1,
    processing_offered:
      leadData.processing_offered ?? dba.processing_offered ?? "",
    avg_month_sales: leadData.avg_month_sales ?? merchant.avg_month_sales ?? "",
    highest_ticket: leadData.highest_ticket ?? dba.highest_ticket ?? "",
    bank_name: leadData.bank_name ?? bank.bank_name ?? "",
    routing_number: leadData.routing_number ?? bank.routing_number ?? "",
    account_number: leadData.account_number ?? bank.account_number ?? "",
    fns_number_for_ebt:
      leadData.fns_number_for_ebt ?? dba.fns_number_for_ebt ?? "",
    dba_address: leadData.dba_address ?? dba.address ?? "",
    additional_note: leadData.additional_note ?? "",
  };
}

function putNum(target, key, val) {
  if (val === "" || val === undefined || val === null) return;
  const n = Number(val);
  if (!Number.isNaN(n)) target[key] = n;
}

/**
 * Backend expects nested groups (same shape as dba[field], legal_information[field], etc.).
 * Attachments are sent as `attachments`: [id, ...] (attachments[0], attachments[1], …).
 */
function buildLeadSubmitPayload(formData) {
  const base = {
    dba_name: formData.dba_name,
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    address: formData.address,
    description: formData.description,
    status_id: formData.status_id,
    source_id: formData.source_id,
    brand_id: formData.brand_id,
    ...(formData.company_id ? { company_id: formData.company_id } : {}),
    ...(formData.country_id ? { country_id: formData.country_id } : {}),
    assignees: formData.assignee_ids,
  };

  const dba = {};
  if (formData.submission_date) dba.submission_date = formData.submission_date;
  if (formData.personal_phone) dba.phone = formData.personal_phone;
  if (formData.dba_name) dba.dba_name = formData.dba_name;
  if (formData.monthly_lease !== "" && formData.monthly_lease != null)
    dba.monthly_lease = formData.monthly_lease;
  if (formData.is_bundled) dba.is_bundled = true;
  if (formData.processing_offered)
    dba.processing_offered = formData.processing_offered;
  if (formData.monthly_volume) dba.monthly_volume = formData.monthly_volume;
  if (formData.highest_ticket) dba.highest_ticket = formData.highest_ticket;
  if (formData.fns_number_for_ebt)
    dba.fns_number_for_ebt = formData.fns_number_for_ebt;
  if (formData.dba_address) dba.address = formData.dba_address;

  const legal_information = {};
  if (formData.legal_business_name)
    legal_information.name = formData.legal_business_name;
  if (formData.legal_business_phone)
    legal_information.phone = formData.legal_business_phone;
  if (formData.legal_business_address)
    legal_information.address = formData.legal_business_address;
  if (formData.legal_business_email)
    legal_information.email = formData.legal_business_email;

  const merchant = {};
  if (formData.federal_tax_id)
    merchant.federal_tax_id = formData.federal_tax_id;
  putNum(merchant, "business_years", formData.business_years);
  putNum(merchant, "business_months", formData.business_months);
  if (formData.business_category)
    merchant.business_category = formData.business_category;
  putNum(merchant, "avg_month_sales", formData.avg_month_sales);

  const bank_account = {};
  if (formData.bank_name) bank_account.bank_name = formData.bank_name;
  if (formData.routing_number)
    bank_account.routing_number = formData.routing_number;
  if (formData.account_number)
    bank_account.account_number = formData.account_number;

  const equipmentRow = {};
  if (formData.equipment_type_id)
    equipmentRow.equipment_type_id = formData.equipment_type_id;
  if (formData.manufacturer_id)
    equipmentRow.manufacturer_id = formData.manufacturer_id;
  if (formData.equipment_model_id)
    equipmentRow.equipment_model_id = formData.equipment_model_id;

  const payload = { ...base };

  if (Object.keys(dba).length) payload.dba = dba;
  if (Object.keys(legal_information).length)
    payload.legal_information = legal_information;
  if (Object.keys(merchant).length) payload.merchant = merchant;
  if (Object.keys(bank_account).length) payload.bank_account = bank_account;
  if (Object.keys(equipmentRow).length) payload.equipment = [equipmentRow];

  if (formData.additional_note !== "" && formData.additional_note != null) {
    payload.additional_note = formData.additional_note;
  }

  if (Array.isArray(formData.attachment_ids)) {
    payload.attachments = [...formData.attachment_ids];
  }

  return payload;
}

/**
 * Server sends nested keys (dba.phone -> dba_phone after normalization);
 * map them back onto the flat form field names so the message lands on the field.
 */
const SERVER_FIELD_TO_FORM = {
  assignees: "assignee_ids",
  dba_submission_date: "submission_date",
  dba_phone: "personal_phone",
  dba_monthly_lease: "monthly_lease",
  dba_processing_offered: "processing_offered",
  dba_monthly_volume: "monthly_volume",
  dba_highest_ticket: "highest_ticket",
  dba_fns_number_for_ebt: "fns_number_for_ebt",
  dba_address: "dba_address",
  legal_information_name: "legal_business_name",
  legal_information_phone: "legal_business_phone",
  legal_information_address: "legal_business_address",
  legal_information_email: "legal_business_email",
  merchant_federal_tax_id: "federal_tax_id",
  merchant_business_years: "business_years",
  merchant_business_months: "business_months",
  merchant_business_category: "business_category",
  merchant_avg_month_sales: "avg_month_sales",
  bank_account_bank_name: "bank_name",
  bank_account_routing_number: "routing_number",
  bank_account_account_number: "account_number",
};

const mapServerErrorsToForm = (serverErrors) =>
  Object.fromEntries(
    Object.entries(serverErrors || {}).map(([key, msg]) => [
      SERVER_FIELD_TO_FORM[key] || key,
      msg,
    ]),
  );

const CreateLead = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [fetchCountriesTrigger] = useLazyGetCountriesQuery();
  const [fetchCompaniesTrigger] = useLazyGetCompaniesQuery();
  const [fetchLeadsStatus] = useLazyGetLeadsStatusQuery();
  const [fetchLeadSources] = useLazyGetLeadSourcesQuery();
  const [fetchLeadBrands] = useLazyGetLeadBrandsQuery();
  const [fetchLeadById] = useLazyGetLeadByIdQuery();
  const [fetchLeadAssignedUsers] = useLazyGetLeadAssignedUsersQuery();
  const [fetchUsersByRole] = useLazyGetUsersByRoleQuery();
  const [createLead] = useCreateLeadMutation();
  const [updateLead] = useUpdateLeadMutation();

  const statusSelect = useLazySelectOptions(fetchLeadsStatus, {
    args: { "conditions[model]": "Lead", paginate: 0 },
    mapResult: mapIdNameList,
  });
  const salesUsersSelect = useLazySelectOptions(fetchUsersByRole, {
    args: "Sales",
    mapResult: mapSalesUsers,
  });
  const sourceSelect = useLazySelectOptions(fetchLeadSources, {
    mapResult: mapIdNameList,
  });
  const brandSelect = useLazySelectOptions(fetchLeadBrands, {
    mapResult: mapIdNameList,
  });
  const companySelect = useLazySelectOptions(fetchCompaniesTrigger, {
    mapResult: mapCountriesOrCompanies,
  });
  const countrySelect = useLazySelectOptions(fetchCountriesTrigger, {
    mapResult: mapCountriesOrCompanies,
  });

  const statusOptions = statusSelect.options;
  const frontSellerOptions = salesUsersSelect.options;
  const sourceOptions = sourceSelect.options;
  const brandOptions = brandSelect.options;
  const companyOptions = companySelect.options;
  const countryOptions = countrySelect.options;

  // const canViewList = hasPermission("user-create");
  const isEditMode = Boolean(id);
  const isViewMode =
    window.location.pathname.includes("/view/") &&
    !window.location.pathname.includes("/edit/");

  const isLeadGenUser = (user?.roles || []).some((r) => {
    const n = (typeof r === "string" ? r : r?.name) || "";
    return n.toLowerCase().trim() === "lead gen";
  });
  /** Lead Gen: on create only, status is fixed to Pending and the field is hidden */
  const hideStatusOnLeadGenCreate = !isEditMode && !isViewMode && isLeadGenUser;

  const [formData, setFormData] = useState({
    dba_name: "",
    name: "",
    email: "",
    phone: "",
    address: "",
    description: "",
    status_id: "",
    source_id: "",
    brand_id: "",
    company_id: "",
    country_id: "",
    assignee_ids: user?.id ? [user.id] : [],
    submission_date: "",
    legal_business_name: "",
    // dba_name: "",
    federal_tax_id: "",
    business_years: "",
    business_months: "",
    business_category: "",
    monthly_volume: "",
    equipment_type_id: "",
    manufacturer_id: "",
    equipment_model_id: "",
    personal_phone: "",
    legal_business_phone: "",
    legal_business_address: "",
    legal_business_email: "",
    monthly_lease: "",
    is_bundled: false,
    processing_offered: "",
    avg_month_sales: "",
    highest_ticket: "",
    bank_name: "",
    routing_number: "",
    account_number: "",
    fns_number_for_ebt: "",
    dba_address: "",
    additional_note: "",
    attachment_ids: [],
    attachments: [],
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEditMode || isViewMode);
  const [submitting, setSubmitting] = useState(false);
  const assigneeSeedRef = useRef(false);

  const applyCreateDefaultsFromList = useCallback(
    (field, list) => {
      if (isEditMode || isViewMode || !list?.length) return;
      setFormData((prev) => {
        if (field === "status" && !prev.status_id) {
          const pending = findPendingStatus(list);
          if (pending) return { ...prev, status_id: pending.id };
        }
        if (field === "brand" && !prev.brand_id) {
          const reviveBrand = findRevivePayBrand(list);
          if (reviveBrand) return { ...prev, brand_id: reviveBrand.id };
        }
        return prev;
      });
    },
    [isEditMode, isViewMode],
  );

  const handleStatusOpen = useCallback(() => {
    statusSelect
      .load()
      .then((list) => applyCreateDefaultsFromList("status", list))
      .catch((err) => console.error("Error fetching status data:", err));
  }, [statusSelect, applyCreateDefaultsFromList]);

  const handleBrandOpen = useCallback(() => {
    brandSelect
      .load()
      .then((list) => applyCreateDefaultsFromList("brand", list))
      .catch((err) => console.error("Error fetching brands data:", err));
  }, [brandSelect, applyCreateDefaultsFromList]);

  useEffect(() => {
    if ((isEditMode || isViewMode) && id) {
      fetchLeadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode, isViewMode]);

  useEffect(() => {
    if (
      assigneeSeedRef.current ||
      isEditMode ||
      isViewMode ||
      !user?.id
    ) {
      return;
    }
    const uid = user.uuid ?? user.id;
    const hasAssignee = (formData.assignee_ids || []).some((aid) =>
      idsEqual(aid, uid),
    );
    if (!hasAssignee) return;
    assigneeSeedRef.current = true;
    salesUsersSelect.setOptions([
      {
        id: uid,
        name: user.name || user.username || "—",
        role: user.roles?.[0]?.name || user.role || "Sales",
      },
    ]);
  }, [user, isEditMode, isViewMode, formData.assignee_ids, salesUsersSelect]);

  const fetchLeadData = async () => {
    try {
      setLoading(true);
      const response = await fetchLeadById(id).unwrap();
      const leadData = extractData(response);

      if (leadData?.id) {
        let assigneeIds = [];
        if (
          Array.isArray(leadData?.assignees) &&
          leadData.assignees.length > 0
        ) {
          assigneeIds = leadData.assignees
            .map((assignee) => assignee.uuid ?? assignee.id)
            .filter(Boolean);
        }
        try {
          const assignedRes = await fetchLeadAssignedUsers(id).unwrap();
          const logs =
            assignedRes?.data?.data?.status_logs ??
            assignedRes?.data?.status_logs ??
            assignedRes?.status_logs ??
            [];
          const ids = [
            ...new Set(
              logs
                .map((s) => s.assignee?.uuid ?? s.assignee?.id ?? s.user_id)
                .filter(Boolean),
            ),
          ];
          if (ids.length) assigneeIds = ids;
        } catch (_) {
          // keep assigneeIds from lead.assignees if fetch fails
        }

        const enriched = enrichSelectOptionsFromLead(leadData, {
          statusOptions: [],
          sourceOptions: [],
          brandOptions: [],
          countryOptions: [],
          companyOptions: [],
          frontSellerOptions: [],
        });
        statusSelect.setOptions(enriched.statusOptions);
        sourceSelect.setOptions(enriched.sourceOptions);
        brandSelect.setOptions(enriched.brandOptions);
        countrySelect.setOptions(enriched.countryOptions);
        companySelect.setOptions(enriched.companyOptions);
        salesUsersSelect.setOptions(enriched.frontSellerOptions);

        const mapped = mapLeadApiToForm(leadData);
        setFormData({
          ...mapped,
          status_id: matchOptionId(enriched.statusOptions, mapped.status_id),
          source_id: matchOptionId(enriched.sourceOptions, mapped.source_id),
          brand_id: matchOptionId(enriched.brandOptions, mapped.brand_id),
          country_id: matchOptionId(enriched.countryOptions, mapped.country_id),
          company_id: matchOptionId(enriched.companyOptions, mapped.company_id),
          assignee_ids: assigneeIds.map((aid) =>
            matchOptionId(enriched.frontSellerOptions, aid),
          ),
          attachment_ids: extractLeadAttachmentIds(leadData),
          attachments: Array.isArray(leadData.attachments)
            ? leadData.attachments
            : [],
        });
      }
    } catch (error) {
      console.error("Error fetching lead data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleAssigneesChange = (event, value) => {
    setFormData((prev) => ({
      ...prev,
      assignee_ids: value ? value.map((v) => v.id) : [],
    }));

    if (errors.assignee_ids) {
      setErrors((prev) => ({
        ...prev,
        assignee_ids: "",
      }));
    }
  };

  // Find the selected assignees from frontSellerOptions
  const getSelectedAssignees = () => {
    return (formData.assignee_ids || [])
      .map((id) => findOptionById(frontSellerOptions, id))
      .filter(Boolean);
  };

  // Helper function to get display value for selects in view mode
  const getDisplayValue = (options, selectedId) => {
    const selectedOption = findOptionById(options, selectedId);
    return selectedOption ? selectedOption.name : "N/A";
  };

  const autocompleteOptionEqual = (option, value) =>
    idsEqual(option?.id, value?.id);

  const getAssigneesDisplayValue = () => {
    const selected = getSelectedAssignees();
    if (!selected.length) return "N/A";
    return selected.map((s) => s.name).join(", ");
  };

  const validateForm = (data = formData) => {
    const newErrors = {};
    if (!data.dba_name.trim())
      newErrors.dba_name = "Business Name is required";
    if (!data.name.trim()) newErrors.name = "Name is required";
    if (!data.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(data.email))
      newErrors.email = "Email is invalid";
    if (!data.phone.trim()) newErrors.phone = "Phone is required";
    if (!hideStatusOnLeadGenCreate && !data.status_id)
      newErrors.status_id = "Status is required";
    if (!data.source_id) newErrors.source_id = "Source is required";
    if (!data.brand_id) newErrors.brand_id = "Brand is required";
    if (!data.country_id) newErrors.country_id = "Country is required";
    if (!data.assignee_ids?.length)
      newErrors.assignee_ids = "At least one assignee is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let submitData = formData;
    if (hideStatusOnLeadGenCreate && !submitData.status_id) {
      try {
        const list = await statusSelect.load();
        const pending = findPendingStatus(list);
        if (pending) {
          submitData = { ...submitData, status_id: pending.id };
          setFormData(submitData);
        }
      } catch (error) {
        console.error("Error resolving Pending status:", error);
      }
    }

    if (validateForm(submitData)) {
      try {
        setSubmitting(true);

        const merged = buildLeadSubmitPayload(submitData);

        let response;
        if (isEditMode) {
          response = await updateLead({ id, data: merged }).unwrap();
        } else {
          response = await createLead(merged).unwrap();
        }

        if (response.success) {
          toast.success(
            response.message ||
              `Lead ${isEditMode ? "updated" : "created"} successfully`,
          );
          navigate("/leads");
        } else {
          toast.error(
            response.message ||
              `Failed to ${isEditMode ? "update" : "create"} lead`,
          );
        }
      } catch (error) {
        const { message, errors: serverErrors } =
          getValidationErrorDetails(error);
        toast.error(message);
        const formErrors = mapServerErrorsToForm(serverErrors);
        if (Object.keys(formErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...formErrors }));
        }
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    navigate("/leads");
  };

  const handleEdit = () => {
    navigate(`/leads/edit/${id}`);
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length > 10) value = value.slice(0, 10);
    setFormData((prev) => ({ ...prev, phone: value }));
  };

  // All fields are disabled in view mode or during loading
  const isDisabled = isViewMode || loading;

  // Skeleton loading component for form fields
  const renderSkeletonField = (width = "100%") => (
    <Skeleton
      variant="rectangular"
      width={width}
      height={40}
      animation="wave"
    />
  );

  const renderSkeletonSelect = () => (
    <Skeleton variant="rectangular" width="100%" height={56} animation="wave" />
  );

  const renderSkeletonTextArea = () => (
    <Skeleton variant="rectangular" width="100%" height={80} animation="wave" />
  );

  // View mode styling
  const viewModeTitleStyle = {
    color: "text.secondary",
    fontWeight: 600,
    mb: 1,
  };

  const viewModeFieldStyle = {
    p: 1.5,
    border: "1px solid",
    borderColor: "grey.300",
    borderRadius: 1,
    minHeight: "40px",
    display: "flex",
    alignItems: "center",
    backgroundColor: "grey.50",
    color: "text.primary",
  };

  const viewModeTextAreaStyle = {
    ...viewModeFieldStyle,
    minHeight: "80px",
    alignItems: "flex-start",
  };

  const capitalizeWords = (text) => {
    if (!text) return "";
    return text.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <PageLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header with Skeleton */}
        <Box
          sx={{
            mb: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {loading ? (
            <Skeleton variant="text" width={200} height={40} animation="wave" />
          ) : (
            <Typography
              variant="h4"
              sx={{
                color: isViewMode ? "text.secondary" : "blue.600",
                fontWeight: 600,
                mb: 1,
                opacity: isViewMode ? 0.7 : 1,
              }}
            >
              {isViewMode ? "View Lead" : isEditMode ? "Edit Lead" : "Add Lead"}
            </Typography>
          )}

          {isViewMode && !loading && (
            <Button
              variant="contained"
              onClick={handleEdit}
              sx={{ bgcolor: "blue.600", "&:hover": { bgcolor: "blue.700" } }}
            >
              Edit Lead
            </Button>
          )}
          {isViewMode && loading && (
            <Skeleton
              variant="rectangular"
              width={120}
              height={36}
              animation="wave"
            />
          )}
        </Box>

        <form onSubmit={handleSubmit}>
          <Card
            sx={{
              boxShadow: isViewMode ? 1 : 2,
              border: isViewMode ? "1px solid" : "none",
              borderColor: isViewMode ? "grey.300" : "transparent",
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonField()
                  ) : isViewMode ? (
                    <>
                      <Typography variant="subtitle1" sx={viewModeTitleStyle}>
                        Business Name
                      </Typography>
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {formData.dba_name || "N/A"}
                      </Typography>
                    </>
                  ) : (
                    <TextField
                      label="Business Name"
                      fullWidth
                      placeholder="Enter DBA name (Doing Business As)"
                      value={formData.dba_name}
                      onChange={handleChange("dba_name")}
                      error={!!errors.dba_name}
                      helperText={errors.dba_name}
                      disabled={isDisabled}
                    />
                  )}
                </Grid>
                {/* Name Field */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonField()
                  ) : isViewMode ? (
                    <Typography variant="body1" sx={viewModeFieldStyle}>
                      {formData.name || "N/A"}
                    </Typography>
                  ) : (
                    <TextField
                      label="Enter lead name"
                      fullWidth
                      placeholder="Enter lead name"
                      value={formData.name}
                      onChange={handleChange("name")}
                      error={!!errors.name}
                      helperText={errors.name}
                      disabled={isDisabled}
                    />
                  )}
                </Grid>

                {/* Email Field */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonField()
                  ) : isViewMode ? (
                    <Typography variant="body1" sx={viewModeFieldStyle}>
                      {formData.email || "N/A"}
                    </Typography>
                  ) : (
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      placeholder="name@example.com"
                      value={formData.email}
                      onChange={handleChange("email")}
                      error={!!errors.email}
                      helperText={errors.email}
                      disabled={isDisabled}
                    />
                  )}
                </Grid>

                {/* Phone Field */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonField()
                  ) : isViewMode ? (
                    <Typography variant="body1" sx={viewModeFieldStyle}>
                      {formData.phone || "N/A"}
                    </Typography>
                  ) : (
                    <TextField
                      fullWidth
                      label="Phone"
                      type="tel"
                      placeholder="1234567890"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      error={!!errors.phone}
                      helperText={errors.phone}
                      disabled={isDisabled}
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        maxLength: 15,
                      }}
                    />
                  )}
                </Grid>

                {/* Country Field */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonSelect()
                  ) : isViewMode ? (
                    <>
                      <Typography variant="subtitle1" sx={viewModeTitleStyle}>
                        Country
                      </Typography>
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {getDisplayValue(countryOptions, formData.country_id)}
                      </Typography>
                    </>
                  ) : (
                    <Autocomplete
                      options={countryOptions}
                      getOptionLabel={(option) => option.name || ""}
                      isOptionEqualToValue={autocompleteOptionEqual}
                      value={findOptionById(
                        countryOptions,
                        formData.country_id,
                      )}
                      onOpen={() => {
                        countrySelect.load().catch((err) =>
                          console.error("Error fetching countries:", err),
                        );
                      }}
                      loading={countrySelect.loading}
                      onChange={(event, newValue) => {
                        setFormData({
                          ...formData,
                          country_id: newValue ? newValue.id : "",
                        });
                        if (errors.country_id) {
                          setErrors((prev) => ({ ...prev, country_id: "" }));
                        }
                      }}
                      disabled={isDisabled}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select country *"
                          error={!!errors.country_id}
                          helperText={errors.country_id}
                          fullWidth
                        />
                      )}
                    />
                  )}
                </Grid>

                {/* Source Field */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonSelect()
                  ) : isViewMode ? (
                    <Typography variant="body1" sx={viewModeFieldStyle}>
                      {capitalizeWords(
                        getDisplayValue(sourceOptions, formData.source_id),
                      )}
                    </Typography>
                  ) : (
                    <Autocomplete
                      options={sourceOptions}
                      getOptionLabel={(option) =>
                        capitalizeWords(option.name || "")
                      }
                      isOptionEqualToValue={autocompleteOptionEqual}
                      value={findOptionById(sourceOptions, formData.source_id)}
                      onOpen={() => {
                        sourceSelect.load().catch((err) =>
                          console.error("Error fetching source data:", err),
                        );
                      }}
                      loading={sourceSelect.loading}
                      onChange={(event, newValue) => {
                        setFormData({
                          ...formData,
                          source_id: newValue ? newValue.id : "",
                        });
                      }}
                      disabled={isDisabled}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select source"
                          error={!!errors.source_id}
                          helperText={errors.source_id}
                          fullWidth
                        />
                      )}
                    />
                  )}
                </Grid>

                {/* Address Field - NEW FIELD ADDED */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonField()
                  ) : isViewMode ? (
                    <>
                      <Typography variant="subtitle1" sx={viewModeTitleStyle}>
                        Address
                      </Typography>
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {formData.address || "N/A"}
                      </Typography>
                    </>
                  ) : (
                    <TextField
                      fullWidth
                      label="Address"
                      placeholder="Enter complete address"
                      value={formData.address}
                      onChange={handleChange("address")}
                      error={!!errors.address}
                      helperText={errors.address}
                      disabled={isDisabled}
                      multiline
                      rows={3}
                    />
                  )}
                </Grid>

                {/* Description Field */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonTextArea()
                  ) : isViewMode ? (
                    <Typography variant="body1" sx={viewModeTextAreaStyle}>
                      {formData.description || "N/A"}
                    </Typography>
                  ) : (
                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      rows={3}
                      placeholder="Enter description"
                      value={formData.description}
                      onChange={handleChange("description")}
                      error={!!errors.description}
                      helperText={errors.description}
                      disabled={isDisabled}
                    />
                  )}
                </Grid>

                {/* Brand Field */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonSelect()
                  ) : isViewMode ? (
                    <Typography variant="body1" sx={viewModeFieldStyle}>
                      {capitalizeWords(
                        getDisplayValue(brandOptions, formData.brand_id),
                      )}
                    </Typography>
                  ) : (
                    <Autocomplete
                      options={brandOptions}
                      getOptionLabel={(option) =>
                        capitalizeWords(option.name || "")
                      }
                      isOptionEqualToValue={autocompleteOptionEqual}
                      value={findOptionById(brandOptions, formData.brand_id)}
                      onOpen={handleBrandOpen}
                      loading={brandSelect.loading}
                      onChange={(event, newValue) => {
                        setFormData({
                          ...formData,
                          brand_id: newValue ? newValue.id : "",
                        });
                      }}
                      disabled={isDisabled}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select brand"
                          error={!!errors.brand_id}
                          helperText={errors.brand_id}
                          fullWidth
                        />
                      )}
                    />
                  )}
                </Grid>

                {/* Company Field */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonSelect()
                  ) : isViewMode ? (
                    <Typography variant="body1" sx={viewModeFieldStyle}>
                      {capitalizeWords(
                        getDisplayValue(companyOptions, formData.company_id),
                      )}
                    </Typography>
                  ) : (
                    <Autocomplete
                      options={companyOptions}
                      getOptionLabel={(option) =>
                        capitalizeWords(option.name || "")
                      }
                      isOptionEqualToValue={autocompleteOptionEqual}
                      value={findOptionById(
                        companyOptions,
                        formData.company_id,
                      )}
                      onOpen={() => {
                        companySelect.load().catch((err) =>
                          console.error("Error fetching companies:", err),
                        );
                      }}
                      loading={companySelect.loading}
                      onChange={(event, newValue) => {
                        setFormData({
                          ...formData,
                          company_id: newValue ? newValue.id : "",
                        });
                        if (errors.company_id) {
                          setErrors((prev) => ({ ...prev, company_id: "" }));
                        }
                      }}
                      disabled={isDisabled}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select company"
                          error={!!errors.company_id}
                          helperText={errors.company_id}
                          fullWidth
                        />
                      )}
                    />
                  )}
                </Grid>

                {/* Status Field — hidden on create for Lead Gen (auto Pending) */}
                {(isEditMode || isViewMode || !isLeadGenUser) && (
                  <Grid item xs={12} md={6}>
                    {loading ? (
                      renderSkeletonSelect()
                    ) : isViewMode ? (
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {capitalizeWords(
                          getDisplayValue(statusOptions, formData.status_id),
                        )}
                      </Typography>
                    ) : (
                      <Autocomplete
                        options={statusOptions}
                        getOptionLabel={(option) =>
                          capitalizeWords(option.name || "")
                        }
                        isOptionEqualToValue={autocompleteOptionEqual}
                        value={findOptionById(
                          statusOptions,
                          formData.status_id,
                        )}
                        onOpen={handleStatusOpen}
                        loading={statusSelect.loading}
                        onChange={(event, newValue) => {
                          setFormData({
                            ...formData,
                            status_id: newValue ? newValue.id : "",
                          });
                        }}
                        disabled={isDisabled}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select status *"
                            error={!!errors.status_id}
                            helperText={errors.status_id}
                            fullWidth
                          />
                        )}
                      />
                    )}
                  </Grid>
                )}

                {/* Lead Assign To Field - multiple assignees */}
                {/* {canViewList ?? */}
                <Grid item xs={12} md={6}>
                  {loading ? (
                    renderSkeletonSelect()
                  ) : isViewMode ? (
                    <Typography variant="body1" sx={viewModeFieldStyle}>
                      {getAssigneesDisplayValue()}
                    </Typography>
                  ) : (
                    <FormControl fullWidth>
                      <Autocomplete
                        multiple
                        options={frontSellerOptions}
                        getOptionLabel={(option) =>
                          `${option.name}${option.role ? `(${option.role})` : ""}`
                        }
                        value={getSelectedAssignees()}
                        onOpen={() => {
                          salesUsersSelect.load().catch((err) =>
                            console.error("Failed to load Sales users:", err),
                          );
                        }}
                        loading={salesUsersSelect.loading}
                        onChange={handleAssigneesChange}
                        disabled={isDisabled}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search and select assignees"
                            error={!!errors.assignee_ids}
                            label="Select assignees"
                          />
                        )}
                        isOptionEqualToValue={autocompleteOptionEqual}
                        noOptionsText="No assignee found"
                      />
                      {errors.assignee_ids && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {errors.assignee_ids}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                </Grid>
                {/* } */}

                {/* <Grid item xs={12}>
                  <LeadAdditionalDetailsSection
                    formData={formData}
                    setFormData={setFormData}
                    disabled={isDisabled}
                    isViewMode={isViewMode}
                    leadId={isEditMode || isViewMode ? id : null}
                    attachmentsHydrateToken={attachmentsHydrate}
                  />
                </Grid> */}
              </Grid>
            </CardContent>
          </Card>

          {/* Action Buttons with Skeletons */}
          {!isViewMode && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
              }}
            >
              {loading ? (
                <>
                  <Skeleton
                    variant="rectangular"
                    width={120}
                    height={36}
                    animation="wave"
                  />
                  <Skeleton
                    variant="rectangular"
                    width={120}
                    height={36}
                    animation="wave"
                  />
                </>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{ minWidth: "120px" }}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      minWidth: "120px",
                      bgcolor: "blue.600",
                      "&:hover": { bgcolor: "blue.700" },
                    }}
                    disabled={submitting}
                  >
                    {submitting
                      ? isEditMode
                        ? "Updating..."
                        : "Adding..."
                      : isEditMode
                        ? "Update Lead"
                        : "Add Lead"}
                  </Button>
                </>
              )}
            </Box>
          )}

          {/* Back Button for View Mode with Skeleton */}
          {isViewMode && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
              }}
            >
              {loading ? (
                <Skeleton
                  variant="rectangular"
                  width={120}
                  height={36}
                  animation="wave"
                />
              ) : (
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{ minWidth: "120px" }}
                >
                  Back to Leads
                </Button>
              )}
            </Box>
          )}
        </form>
      </Container>
    </PageLayout>
  );
};

export default CreateLead;
